import React, { useEffect, useState } from 'react';
import { Layout, message, Button, Form, Input, Switch, Select, Tabs, Drawer } from 'antd';
import { PageHeader } from '@ant-design/pro-components';
import { useNavigate, useParams } from 'react-router-dom';
import MemberSidebar from './components/member.sidebar.component';
import MemberFooter from './components/member.footer.component';
import { useDispatch, useSelector } from 'react-redux';
import dot from 'dot-object';
import MediaSelect from './components/mediaSelect.field';
import EntityMedia from './entity.media.page';
import { API_CDN_URL } from '../services/config';
import {
    readTripTopic,
    createTripTopic,
    updateTripTopic
} from '../actions/entity.trip.topic'
import {
    readAllActiveTripCountriesTranslations
} from '../actions/entity.trip.country';
import { userIsReadOnlyInEntity } from '../helpers/user';
import { useTranslation } from 'react-i18next';
import SeoFields from './components/seo.component';

const layout = {
    labelCol: {
        span: 8,
    },
    wrapperCol: {
        span: 10,
    },
};

function TripTopic() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const entities = useSelector(state => state.entity.entities);
    const currentEntity = entities ? entities.find(entity => entity.id === params.entityId) : null;
    const { Content } = Layout;
    const [form] = Form.useForm();
    const [tripTopicTitle, setTripTopicTitle] = useState('');
    const currentUser = useSelector(state => state.auth.user);
    const userIsReadOnly = userIsReadOnlyInEntity(currentUser, currentEntity);
    const requiredTripSupportAllLanguages = currentEntity && currentEntity.requiredTripSupportAllLanguages;
    const [addedLanguages, setAddedLanguages] = useState([]);
    const [mediaDrawerVisible, setMediaDrawerVisible] = useState(false);
    const [currentMediaFieldName, setCurrentMediaFieldName] = useState(null);
    const [topicData, setTopicData] = useState(null);

    const showMediaDrawer = (fieldName) => {
        setCurrentMediaFieldName(fieldName);
        setMediaDrawerVisible(true);
    };
    const onMediaDrawerClose = () => {
        setMediaDrawerVisible(false);
    };

    const onSelectMedia = (media) => {
        setMediaDrawerVisible(false);
        if (currentMediaFieldName && currentEntity) {
            let url = API_CDN_URL(currentEntity) + media.path;
            let newValue = {
                [currentMediaFieldName.join('.')]: url
            };
            newValue = dot.object(newValue);
            form.setFieldsValue(newValue);
        }
    }

    useEffect(() => {
        if (currentEntity) {
            if (currentEntity.requiredTripSupportAllLanguages) {
                setAddedLanguages(currentEntity.languages.filter(language => {
                    return language.status
                }).map(language => {
                    return {
                        label: language.name,
                        value: language.code
                    }
                }));
            } else {
                if (topicData && typeof topicData.languages !== 'undefined' && Array.isArray(topicData.languages)) {
                    setAddedLanguages(currentEntity.languages.filter(language => {
                        return language.status && topicData.languages.includes(language.code)
                    }).map(language => {
                        return {
                            label: language.name,
                            value: language.code
                        }
                    }));
                }
            }
        }
    }, [currentEntity, topicData]);
    const languageOptions = !requiredTripSupportAllLanguages && currentEntity && currentEntity.languages ? currentEntity.languages.map(language => {
        return {
            label: language.name,
            value: language.code,
            disabled: !language.status
        }
    }) : [];

    const onFinish = (values) => {
        if (userIsReadOnly) {
            message.error(t('tripTopic.message.read-only'));
            return;
        }
        if (typeof params.topicId !== 'undefined') {
            // Update the existing entity topic
            dispatch(updateTripTopic(params.entityId, params.topicId, values)).then((response) => {
                if (typeof response.result !== 'undefined' && typeof response.result.id !== 'undefined') {
                    setTripTopicTitle(t('tripTopic.title.edit'));
                    form.setFieldsValue(response.result);
                    setTopicData(response.result);
                }
                message.success(t('tripTopic.message.update-success'));
            }).catch(error => {
                try {
                    let responseMessage = error.response.data.message;
                    message.error(responseMessage);
                } catch (e) {
                    console.log('Failed:', e);
                    message.error(JSON.stringify(e));
                }
            });
        } else {
            // Create new campaign
            dispatch(createTripTopic(params.entityId, values)).then((data) => {
                message.success(t('tripTopic.message.create-success'));
                if (data.result) {
                    navigate(`/entity/${params.entityId}/trip-topic/${data.result.id}`);
                } else {
                    navigate(`/entity/${params.entityId}/trip-topics`);
                }
            }).catch(error => {
                try {
                    let responseMessage = error.response.data.message;
                    message.error(responseMessage);
                } catch (e) {
                    console.log('Failed:', e);
                    message.error(JSON.stringify(e));
                }
            });
        }
    }

    useEffect(() => {
        if (typeof params.topicId !== 'undefined') {
            setTripTopicTitle(t('tripTopic.title.edit'));
            dispatch(readTripTopic(params.entityId, params.topicId)).then((response) => {
                if (typeof response.result !== 'undefined' && typeof response.result.id !== 'undefined') {
                    setTripTopicTitle(t('tripTopic.title.edit'));
                    form.setFieldsValue(response.result);
                    setTopicData(response.result);
                } else {
                    message.error(t('tripTopic.message.error-reading'));
                    navigate(`/entity/${params.entityId}/trip-topics`);
                }
            }).catch(e => {
                message.error(t('tripTopic.message.error-reading'));
                navigate(`/entity/${params.entityId}/trip-topics`);
            });
        } else {
            setTripTopicTitle(t('tripTopic.title.new'));
        }
    }, [params.topicId, params.entityId, dispatch, navigate, form]);

    const onFinishFailed = (errorInfo) => {
        if (typeof errorInfo.errorFields !== 'undefined') {
            errorInfo.errorFields.forEach(field => {
                message.error(field.errors[0]);
            });
        }
    }

    const selectedLanguages = (options) => {
        if (Array.isArray(options)) {
            const filteredLanguages = languageOptions.filter(language => {
                if (options.includes(language.value)) {
                    return true;
                } else {
                    return false;
                }
            });
            console.log(options);
            console.log(filteredLanguages);
            setAddedLanguages(filteredLanguages);
        } else {
            setAddedLanguages([]);
        }
    }

    let extraButtons = [
        <Button key="cancel" type="primary" danger href={`/entity/${params.entityId}/trip-topics`}>{t('tripTopic.actions.cancel')}</Button>
    ];
    if (!userIsReadOnly) {
        extraButtons.push(<Button key="save" type="primary" htmlType="submit">{params.topicId ? t('tripTopic.actions.update') : t('tripTopic.actions.save')}</Button>);
    }
    return <>
        <MemberSidebar />
        <Layout className="site-layout">
            <Content style={{ margin: '0 16px' }}>
                <div className="site-layout-background">
                    <Form
                        {...layout}
                        form={form}
                        name="entity-topic"
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        initialValues={{
                            status: true
                        }}>
                        <PageHeader
                            className="site-page-header"
                            onBack={() => navigate(`/entity/${params.entityId}/trip-topics`)}
                            title={tripTopicTitle}
                            extra={extraButtons}
                        />
                        <>
                            <Form.Item
                                label={t('tripTopic.field.status.label')}
                                name="status"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                            <Form.Item
                                label={t('tripTopic.field.slug.label')}
                                required={true}
                            >
                                <Form.Item
                                    name="slug"
                                    rules={[
                                        {
                                            required: true,
                                            message: t('tripTopic.field.slug.required')
                                        },
                                        { pattern: /^[a-z0-9-_]+$/, message: t('tripTopic.field.slug.pattern') }
                                    ]}
                                    noStyle>
                                    <Input placeholder={t('tripTopic.field.slug.placeholder')} />
                                </Form.Item>
                                <div>{t('tripTopic.field.slug.note')}</div>
                            </Form.Item>
                            {!requiredTripSupportAllLanguages && <Form.Item
                                label={t('tripTopic.field.languages.label')}
                                required={true}
                            >
                                <Form.Item
                                    name="languages"
                                    rules={[
                                        {
                                            required: true,
                                            message: t('tripTopic.field.languages.required')
                                        }
                                    ]}
                                    noStyle>
                                    <Select mode="multiple" allowClear options={languageOptions} onChange={selectedLanguages} />
                                </Form.Item>
                                <div>{t('tripTopic.field.languages.note')}</div>
                            </Form.Item>}
                            <Tabs>
                                {addedLanguages.map((language, index) => {
                                    return <Tabs.TabPane tab={language.label} key={language.value} forceRender>
                                        <Form.Item
                                            label={t('tripTopic.field.title.label')}
                                            name={[language.value, 'title']}
                                            required={true}
                                            rules={
                                                [
                                                    {
                                                        required: true,
                                                        message: t('tripTopic.field.title.required')

                                                    }
                                                ]}
                                        >
                                            <Input placeholder={t('tripTopic.field.title.placeholder')} />
                                        </Form.Item>
                                        <Form.Item
                                            label={t('tripTopic.field.subtitle.label')}
                                            name={[language.value, 'subtitle']}
                                        >
                                            <Input placeholder={t('tripTopic.field.subtitle.placeholder')} />
                                        </Form.Item>
                                        <Form.Item
                                            label={t('tripTopic.field.description.label')}
                                            name={[language.value, 'description']}
                                        >
                                            <Input.TextArea autoSize={{ minRows: 5, maxRows: 10 }} placeholder={t('tripTopic.field.description.placeholder')} />
                                        </Form.Item>
                                        <MediaSelect showMediaDrawer={showMediaDrawer} form={form} wrapper={{
                                            wrapperCol: 16,
                                            required: true,
                                            label: t('tripTopic.field.banner.label')
                                        }} inner={{
                                            name: [language.value, 'banner'],
                                            rules: [
                                                {
                                                    required: true,
                                                    message: t('tripTopic.field.banner.required')
                                                }
                                            ]
                                        }} />
                                        <SeoFields
                                            name={[language.value]}
                                            layout={layout}
                                            form={form}
                                            showMediaDrawer={showMediaDrawer}
                                        />
                                    </Tabs.TabPane>
                                })}
                            </Tabs>
                        </>
                    </Form>
                    <Drawer contentWrapperStyle={{ width: '1000px', maxWidth: '100%', minWidth: '50%' }} title={t('mediaDrawer.title')} placement="right" onClose={onMediaDrawerClose} open={mediaDrawerVisible}>
                        <EntityMedia entityId={params.entityId} selectMedia={onSelectMedia} />
                    </Drawer>
                </div>
            </Content>
            <MemberFooter />
        </Layout>
    </>;
}

export default TripTopic;